import { SolidStatusPill } from '@odo/shared-ui';
import type { DmMessagePatientRow } from '../../../services/dmMessagesInbox';
import { plural } from '../../../utils/plural';
import { NoReplyNeededPill } from './triage/NoReplyNeededPill';

const OTHERS_PILL_TONE = 'border border-gray-200 bg-gray-50 text-gray-600';

// "+N" counts the patient's other unreviewed messages, besides the one previewed.
function OtherMessagesPill({ unreviewedCount }: { unreviewedCount: number }) {
    const others = unreviewedCount - 1;
    if (others <= 0) return null;
    const label = `+${others.toLocaleString()}`;
    const description = `${plural(others, 'more message')} to review`;
    return (
        <span className="flex-shrink-0">
            <span className="sr-only">{description}</span>
            <span aria-hidden="true">
                <SolidStatusPill
                    value={label}
                    label={label}
                    tooltip={description}
                    toneClassName={OTHERS_PILL_TONE}
                    dotClassName="hidden"
                    compact
                />
            </span>
        </span>
    );
}

interface LatestMessageCellProps {
    row: DmMessagePatientRow;
    // A closed row's count and triage aren't about messages to review, so their pills stay off.
    closed?: boolean;
}

// One line in the table; the full text stays reachable on hover.
export function LatestMessageCell({ row, closed = false }: LatestMessageCellProps) {
    const text = row.latestMessage.text?.trim();
    return (
        <span className="flex min-w-0 items-center gap-2">
            {text ? (
                <span className="min-w-0 truncate text-[13px] text-gray-700" title={text}>
                    {text}
                </span>
            ) : (
                <span className="text-[13px] italic text-gray-400">No text</span>
            )}
            {!closed && <OtherMessagesPill unreviewedCount={row.unreviewedCount} />}
            {!closed && row.triage.replyNeeded === false && <NoReplyNeededPill />}
        </span>
    );
}
